import { ProductItem } from 'lane-shared/domains/billingPayments/types';
import { type SupportedPaymentCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';
import { productToPriceComponents } from 'lane-shared/domains/billingPayments/helpers';
import { getPricing } from 'lane-shared/domains/productCatalog/helpers';

/**
 * Create an updated product item with recalculated pricing
 * after a rate override
 */
export const createUpdatedProductItem = (
  item: ProductItem,
  overrideAmount: number,
  currency: SupportedPaymentCurrency,
  locale: SupportedLocaleEnum,
  userInputRate?: number // user input for list
): ProductItem => {
  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (value: number): number => {
    return Math.round(value * 100) / 100;
  };

  // Build/update originalPrice based on the scenario
  let originalPrice = item.product.originalPrice;

  if (userInputRate !== undefined && !item.isRateOverridden) {
    if (!originalPrice || item.product.sellingPrice?.listPrice !== undefined) {
      // Scenario 1: No originalPrice - capture current pricing as original
      // Scenario 2: Has originalPrice + sellingPrice (price exception) - update to reflect exception
      originalPrice = {
        amount: item.product.amount,
        markup: item.product.markup || undefined,
        tax: item.product.tax || undefined,
      };
    }
  }

  const overrideSellingPrice = userInputRate !== undefined
    ? {
      listPrice: userInputRate,
      grossMarkup: roundToTwoDecimals(userInputRate - overrideAmount),
    }
    : undefined;

  const updatedProduct = {
    ...item.product,
    amount: overrideAmount,
    sellingPrice: overrideSellingPrice,
    originalPrice,
  };

  const priceComponents = productToPriceComponents(updatedProduct);

  const { grossMarkup, grossTax, listPrice, netPrice, grossAdminFee } = getPricing({
    currency,
    locale,
    priceComponents,
  });

  const roundedPricing = {
    grossTax: roundToTwoDecimals(grossTax),
    grossMarkup: roundToTwoDecimals(grossMarkup),
    listPrice: roundToTwoDecimals(listPrice),
    netPrice: roundToTwoDecimals(netPrice),
    grossAdminFee: roundToTwoDecimals(grossAdminFee),
  };

  return {
    ...item,
    product: {
      ...updatedProduct,
      amount: roundToTwoDecimals(overrideAmount),
      sellingPrice: roundedPricing,
    },
    isRateOverridden: true,
    // Store the user's original input rate for validation
    userInputRate: userInputRate ?? roundedPricing.listPrice,
  };
};
